import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import {
  makeCoinImageOnError,
  withdrawHistoryTxExplorerEligible,
} from "../WithdrawPage/withdrawPageHelpers";
import "../WithdrawPage/withdrawal.css";

function resolveTxExplorerUrl(network, txid) {
  const n = (network || "").toLowerCase();
  if (n.includes("bsc") || n.includes("bep")) return `https://bscscan.com/tx/${txid}`;
  if (n.includes("trc") || n.includes("tron")) return `https://tronscan.org/#/transaction/${txid}`;
  return `https://etherscan.io/tx/${txid}`;
}

function resolveAddressExplorerUrl(network, address) {
  const n = (network || "").toLowerCase();
  if (n.includes("bsc") || n.includes("bep")) return `https://bscscan.com/address/${address}`;
  if (n.includes("trc") || n.includes("tron")) return `https://tronscan.org/#/address/${address}`;
  return `https://etherscan.io/address/${address}`;
}

function withdrawStatusTone(label) {
  const t = String(label || "").toLowerCase();
  if (!t || t === "—") return "neutral";
  if (/(completed|confirmed|success|succeed|done)/i.test(t)) return "success";
  if (/(pending|processing|confirming|in progress|queued|await)/i.test(t)) return "pending";
  if (/(rejected|reject|failed|fail|cancel|canceled|error|expired)/i.test(t)) return "danger";
  return "info";
}

const WithdrawHistCopyIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
      fill="currentColor"
    />
  </svg>
);

const WithdrawHistExternalIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
    />
  </svg>
);

async function copyWithdrawText(text, successMsg = "Copied") {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    alertSuccessMessage(successMsg);
  } catch {
    /* ignore */
  }
}

/**
 * Same withdrawal detail dialog as `WithdrawPage` recent history (stepper + rows + CTAs).
 * `row` must be the object returned by `mapWithdrawHistoryRow`.
 */
export default function WithdrawHistoryDetailModal({ open, row, onClose, withdrawAgainTo = "/asset_managemnet/withdraw" }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const onWithdrawAgain = () => {
    onClose?.();
    navigate(withdrawAgainTo);
  };

  return (
    <div
      className="withdraw_confirm_overlay withdraw_hist_detail_overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="withdraw_hist_detail_modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="withdraw_hist_detail_title_orders"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>

        <div className="withdraw_hist_detail_head">
          <h2 id="withdraw_hist_detail_title_orders" className="withdraw_hist_detail_title">
            Withdrawal Details
          </h2>
        </div>

        <div className="withdraw_hist_detail_body">
          <div
            className="withdraw_hist_stepper"
            aria-label="Withdrawal progress"
            data-outcome={row.historyOutcomeTone || "pending"}
          >
            {row.steps.map((s, i, arr) => (
              <div key={`${row.id}-step-${i}`} className="withdraw_hist_step">
                <div className="withdraw_hist_step_track" aria-hidden="true">
                  <span className="withdraw_hist_step_dot" />
                  {i < arr.length - 1 ? <span className="withdraw_hist_step_line" /> : null}
                </div>
                <div className="withdraw_hist_step_text">
                  <div className={`withdraw_hist_step_title${s.emphasized ? " is-emphasized" : ""}`}>{s.title}</div>
                  {s.subtitle ? <div className="withdraw_hist_step_sub">{s.subtitle}</div> : null}
                  <div className="withdraw_hist_step_time">{s.time}</div>
                </div>
              </div>
            ))}
          </div>

          {withdrawStatusTone(row.statusLabel) === "danger" ? (
            <p className="withdraw_hist_step_note withdraw_hist_step_note--danger">
              This withdrawal did not succeed. You can try again from your wallet; if anything looks wrong, contact support with below address.
            </p>
          ) : withdrawStatusTone(row.statusLabel) === "success" ? (
            <p className="withdraw_hist_step_note">Your withdrawal has completed. You should see funds at the destination address.</p>
          ) : (
            <p className="withdraw_hist_step_note">Please note that you will receive an email once it is completed.</p>
          )}
          <div className="withdraw_hist_help_links">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Report Scam
            </Link>
          </div>

          <div className="withdraw_hist_rows">
            {(() => {
              const tone = withdrawStatusTone(row.statusLabel);
              const hasAddr = Boolean(row.addressFull);
              const hasTx = Boolean(row.txFull);
              const txEligible =
                typeof row.txExplorerEligible === "boolean"
                  ? row.txExplorerEligible
                  : withdrawHistoryTxExplorerEligible(row.txFull);
              const networkPretty = row.networkLabel || row.network;
              return (
                <>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Status</span>
                    <span className={`withdraw_hist_row_value withdraw_hist_row_value--${tone}`}>{row.statusLabel}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Date</span>
                    <span className="withdraw_hist_row_value">{row.date}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Source</span>
                    <span className="withdraw_hist_row_value">{row.source}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Coin</span>
                    <span className="withdraw_hist_row_value withdraw_hist_row_value--coin">
                      <img
                        src={row.coinIconSrc}
                        alt=""
                        width={22}
                        height={22}
                        onError={makeCoinImageOnError({ short_name: row.coin })}
                      />
                      {row.coin}
                    </span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Withdraw amount</span>
                    <span className="withdraw_hist_row_value">{row.amount}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Network fee</span>
                    <span className="withdraw_hist_row_value">{row.networkFee}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Address</span>
                    <span className="withdraw_hist_row_value withdraw_hist_row_value--addr">
                      <span className="withdraw_hist_mono">{row.addressDisplay}</span>
                      {row.addressUnverified ? (
                        <span className="withdraw_hist_badge_unverified">
                          <span className="withdraw_hist_badge_icon" aria-hidden="true">
                            !
                          </span>
                          Unverified
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="withdraw_hist_icon_btn"
                        aria-label="Copy address"
                        disabled={!hasAddr}
                        onClick={() => (hasAddr ? copyWithdrawText(row.addressFull, "Address copied") : null)}
                      >
                        <WithdrawHistCopyIcon />
                      </button>
                      <a
                        className={`withdraw_hist_icon_btn withdraw_hist_icon_link${!hasAddr ? " is-disabled" : ""}`}
                        href={hasAddr ? resolveAddressExplorerUrl(row.network, row.addressFull) : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View address on explorer"
                        aria-disabled={!hasAddr}
                        onClick={(e) => {
                          if (!hasAddr) e.preventDefault();
                        }}
                      >
                        <WithdrawHistExternalIcon />
                      </a>
                    </span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">Network</span>
                    <span className="withdraw_hist_row_value">{networkPretty}</span>
                  </div>
                  <div className="withdraw_hist_row">
                    <span className="withdraw_hist_row_label">TxID</span>
                    <span className="withdraw_hist_row_value withdraw_hist_row_value--addr">
                      <span className="withdraw_hist_mono">{row.txDisplay}</span>
                      {!txEligible && hasTx ? (
                        <span className="withdraw_hist_tx_ref_hint" title="Internal reference — not an on-chain hash">
                          Reference
                        </span>
                      ) : null}
                      <button
                        type="button"
                        className="withdraw_hist_icon_btn"
                        aria-label="Copy transaction ID"
                        disabled={!hasTx}
                        onClick={() => (hasTx ? copyWithdrawText(row.txFull, "TxID copied") : null)}
                      >
                        <WithdrawHistCopyIcon />
                      </button>
                      <a
                        className={`withdraw_hist_icon_btn withdraw_hist_icon_link${!hasTx || !txEligible ? " is-disabled" : ""}`}
                        href={hasTx && txEligible ? resolveTxExplorerUrl(row.network, row.txFull) : undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View transaction on explorer"
                        aria-disabled={!hasTx || !txEligible}
                        onClick={(e) => {
                          if (!hasTx || !txEligible) e.preventDefault();
                        }}
                      >
                        <WithdrawHistExternalIcon />
                      </a>
                    </span>
                  </div>
                </>
              );
            })()}
          </div>

          <p className="withdraw_hist_chat_row">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Need help? Chat with us
            </Link>
          </p>

          {/* <button type="button" className="withdraw_hist_again_btn" onClick={onWithdrawAgain}>
            Withdrawal Again
          </button> */}
        </div>
      </div>
    </div>
  );
}
